import {
  getFlightSearchState,
  updateFlightSearchState,
} from 'legacyfrontend/helpers/flightSearchStateStorage';
import { parseFlightDateParam } from 'legacyfrontend/flights/search/flightSearchParams';

const normalizeAirport = (airport) => {
  if (!airport) return null;

  return {
    id: airport.id,
    airportCode: airport.airportCode || airport.cityCode,
    airportName: airport.airportName || airport.cityName,
    cityCode: airport.cityCode,
    cityName: airport.cityName,
    countryCode: airport.countryCode,
  };
};

const normalizePassengers = (passengers) => {
  if (Array.isArray(passengers) && passengers.length > 0) return passengers;
  return [30];
};

const normalizeBoolean = (value, fallback = false) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value == null) return fallback;
  return Boolean(value);
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const ORIGIN_METADATA_KEYS = [
  'originId',
  'originName',
  'originAirportCode',
  'originAirportName',
  'originCityCode',
  'originCountryCode',
];

const DESTINATION_METADATA_KEYS = [
  'destinationId',
  'destinationName',
  'destinationAirportCode',
  'destinationAirportName',
  'destinationCityCode',
  'destinationCountryCode',
];

const clearMetadataKeys = (flightInfo, keys) => {
  keys.forEach((key) => {
    delete flightInfo[key];
  });
};

const formatDateLocal = (date) => {
  if (!date) return null;

  if (typeof date === 'string') {
    const dateMatch = date.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) return dateMatch[1];
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseStoredDate = (date) => {
  if (!date) return null;

  if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;

  const normalizedDate = formatDateLocal(date);
  return normalizedDate ? parseFlightDateParam(normalizedDate) : null;
};

const normalizeFlightInfo = (flightInfo) => {
  if (!flightInfo) return null;

  const origin = normalizeAirport(flightInfo.origin);
  const destination = normalizeAirport(flightInfo.destination);
  const isOnlyOneWay = normalizeBoolean(flightInfo.isOnlyOneWay, false);
  const departureDate = formatDateLocal(flightInfo.departureDate);
  const returnDate = isOnlyOneWay ? null : formatDateLocal(flightInfo.returnDate);

  return {
    ...flightInfo,
    origin,
    destination,
    passengers: normalizePassengers(flightInfo.passengers),
    departureDate,
    returnDate,
    isOnlyOneWay,
    currency: flightInfo.currency || 'USD',
    originId: flightInfo.originId || origin?.cityCode,
    originName: flightInfo.originName || origin?.cityName,
    destinationId: flightInfo.destinationId || destination?.cityCode,
    destinationName: flightInfo.destinationName || destination?.cityName,
  };
};

export const getNormalizedFlightSearchState = () => {
  const state = getFlightSearchState();

  return {
    ...state,
    flightInfo: normalizeFlightInfo(state.flightInfo),
  };
};

export const getStoredFlightFormDraft = () => {
  const flightInfo = getNormalizedFlightSearchState().flightInfo;
  if (!flightInfo) return null;

  return {
    origin: flightInfo.origin,
    destination: flightInfo.destination,
    passengers: normalizePassengers(flightInfo.passengers),
    departureDate: parseStoredDate(flightInfo.departureDate),
    returnDate: parseStoredDate(flightInfo.returnDate),
    isOnlyOneWay: normalizeBoolean(flightInfo.isOnlyOneWay, false),
    direct: false,
    baggage: false,
    cabin: 'Y',
    currency: flightInfo.currency || 'USD',
  };
};

export const saveFlightFormDraft = (input) => {
  const currentFlightInfo = getNormalizedFlightSearchState().flightInfo || {};
  const isOnlyOneWay = normalizeBoolean(input?.isOnlyOneWay, currentFlightInfo.isOnlyOneWay);
  const hasOrigin = hasOwn(input, 'origin');
  const hasDestination = hasOwn(input, 'destination');
  const hasDepartureDate = hasOwn(input, 'departureDate');
  const hasReturnDate = hasOwn(input, 'returnDate');
  const origin = hasOrigin && input?.origin
    ? {
      ...normalizeAirport(input.origin),
      countryCode: input.origin.countryCode || currentFlightInfo.origin?.countryCode,
    }
    : hasOrigin
      ? null
      : currentFlightInfo.origin;
  const destination = hasDestination && input?.destination
    ? {
      ...normalizeAirport(input.destination),
      countryCode: input.destination.countryCode || currentFlightInfo.destination?.countryCode,
    }
    : hasDestination
      ? null
      : currentFlightInfo.destination;

  const flightInfo = {
    ...currentFlightInfo,
    origin,
    destination,
    departureDate: hasDepartureDate
      ? formatDateLocal(input.departureDate)
      : currentFlightInfo.departureDate,
    returnDate: isOnlyOneWay
      ? null
      : hasReturnDate
        ? formatDateLocal(input.returnDate)
        : currentFlightInfo.returnDate,
    passengers: input?.passengers ? normalizePassengers(input.passengers) : currentFlightInfo.passengers,
    isOnlyOneWay,
    currency: input?.currency || currentFlightInfo.currency || 'USD',
  };

  if (origin) {
    flightInfo.originId = origin.cityCode;
    flightInfo.originName = origin.cityName;
  } else {
    clearMetadataKeys(flightInfo, ORIGIN_METADATA_KEYS);
  }

  if (destination) {
    flightInfo.destinationId = destination.cityCode;
    flightInfo.destinationName = destination.cityName;
  } else {
    clearMetadataKeys(flightInfo, DESTINATION_METADATA_KEYS);
  }

  return updateFlightSearchState({ flightInfo });
};

export const saveResolvedFlightInfo = (info) => {
  const currentFlightInfo = getNormalizedFlightSearchState().flightInfo || {};
  const normalizedInfo = normalizeFlightInfo({ ...currentFlightInfo, ...info });

  return updateFlightSearchState({ flightInfo: normalizedInfo });
};

export const clearFlightSelectionStateForNewSearch = (flightInfo) => {
  const currentFlightInfo = getNormalizedFlightSearchState().flightInfo || {};
  const normalizedFlightInfo = flightInfo
    ? normalizeFlightInfo({ ...currentFlightInfo, ...flightInfo })
    : currentFlightInfo;

  return updateFlightSearchState({
    flightInfo: normalizedFlightInfo,
    flightSelections: {},
    selectedFare: null,
    expCacheDate: null,
  });
};

export const saveLastEssentialSearchParams = (key) =>
  updateFlightSearchState({ lastEssentialSearchParams: key || '' });
